// Card game and founding member flow (sections/up-founding-game.liquid), two separate flows.
// Cards: the prize is drawn with crypto.getRandomValues when the email is sent, saved as a
// customer tag, and only then revealed by the card the visitor picks.
// Founding: three questions, an answer built from them, then 10% for life.
// Each flow is finished once per browser (localStorage), with its own keys.
const DOTTED = ['q1', 'q2', 'q3', 'solve', 'email'];

const read = (store, key) => { try { return store.getItem(key); } catch (e) { return null; } };
const write = (store, key, value) => { try { store.setItem(key, value); } catch (e) {} };
const slug = (s) => String(s).toLowerCase().replace(/\+/g, 'plus').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
// ?fg=preview: play the whole flow without signing up (no tags, no alert, nothing remembered).
const preview = new URLSearchParams(location.search).get('fg') === 'preview';

function drawTier(odds) {
  const total = odds.reduce((a, b) => a + Math.max(0, b), 0) || 1;
  const r = (crypto.getRandomValues(new Uint32Array(1))[0] / 4294967296) * total;
  let acc = 0;
  for (let i = 0; i < odds.length; i++) {
    acc += Math.max(0, odds[i]);
    if (r < acc) return i;
  }
  return 0;
}

// Email check: trims spaces (including the non-breaking and zero-width ones iPhone keyboards add),
// then needs something@domain.tld. Capitals, dots and plus signs are all fine.
const cleanEmail = (v) => String(v || '').replace(/^[\s\u00a0\u200b\u200c\u200d\ufeff]+|[\s\u00a0\u200b\u200c\u200d\ufeff]+$/g, '');
const looksLikeEmail = (v) => /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(v);

// Run once Shopify's cookie banner is out of the way, so the pop-up never sits under (or over) it.
function whenConsentClear(fn) {
  const showing = () => { const b = document.getElementById('shopify-pc__banner'); return !!(b && b.offsetParent !== null && getComputedStyle(b).visibility !== 'hidden'); };
  if (!showing()) { fn(); return; }
  const t = setInterval(() => { if (!showing()) { clearInterval(t); setTimeout(fn, 400); } }, 500);
}

function init(root) {
  const cfg = JSON.parse(root.querySelector('[data-fg-config]').textContent);
  const cards = cfg.flow === 'cards';
  const key = cards ? 'up_cg' : 'up_fg';
  const PLAYED = `${key}_played`;
  const SEEN = `${key}_seen`;
  const PENDING = `${key}_pending`;
  const playedState = () => { try { return JSON.parse(read(localStorage, PLAYED) || 'null'); } catch (e) { return null; } };
  const STEPS = [...root.querySelectorAll('[data-fg-step]')].map((s) => s.dataset.fgStep);
  const card = root.querySelector('[data-fg-card]');
  const top = root.querySelector('[data-fg-top]');
  const dots = [...root.querySelectorAll('[data-fg-dot]')];
  const steps = Object.fromEntries([...root.querySelectorAll('[data-fg-step]')].map((s) => [s.dataset.fgStep, s]));
  const form = root.querySelector('[data-fg-form]');
  const emailField = root.querySelector('[data-fg-email]');
  const note = root.querySelector('[data-fg-note]');
  const overlay = root.dataset.mode === 'overlay';
  const prompt = overlay ? document.querySelector('[data-fg-prompt]') : null;
  const price = Number(root.dataset.price) || 0;
  const answers = {};
  let current = 'hook';
  let tier = null;
  let lastFocus = null;

  const money = (pence) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: cfg.currency || 'GBP' }).format(pence / 100);

  function go(name, back = false) {
    current = name;
    card.classList.toggle('is-back', back);
    card.classList.toggle('is-silver', name === 'hook');
    Object.entries(steps).forEach(([k, el]) => el.classList.toggle('is-current', k === name));
    if (top) {
      top.hidden = !DOTTED.includes(name);
      const at = DOTTED.indexOf(name);
      dots.forEach((d, i) => { d.classList.toggle('is-done', i < at); d.classList.toggle('is-current', i === at); });
      root.querySelector('[data-fg-back]').hidden = at <= 0 && name !== 'q1';
    }
    const focus = steps[name].querySelector('h2, legend');
    if (focus) requestAnimationFrame(() => focus.focus({ preventScroll: !overlay }));
    if (name === 'solve') buildAnswer();
    if (name === 'email') setTimeout(() => emailField.focus(), 350);
  }

  function next() { go(STEPS[STEPS.indexOf(current) + 1]); }
  function back() {
    const i = STEPS.indexOf(current);
    if (i > 0 && DOTTED.includes(current)) go(STEPS[i - 1], true);
  }

  function buildAnswer() {
    const list = root.querySelector('[data-fg-answer]');
    list.textContent = '';
    ['q1', 'q2', 'q3'].forEach((q, n) => {
      const text = answers[q] && cfg.answers[q][answers[q].index];
      if (!text) return;
      const li = document.createElement('li');
      li.style.setProperty('--i', n);
      li.textContent = text;
      list.append(li);
    });
  }

  // Tap answers move on by themselves.
  ['q1', 'q2', 'q3'].forEach((q) => {
    steps[q]?.addEventListener('change', (e) => {
      const input = e.target.closest('input[type=radio]');
      if (!input) return;
      answers[q] = { index: Number(input.value) - 1, label: input.dataset.label };
      setTimeout(next, reduced ? 0 : 260);
    });
  });
  root.querySelectorAll('[data-fg-next]').forEach((b) => b.addEventListener('click', next));
  root.querySelector('[data-fg-back]')?.addEventListener('click', back);

  function tagsFor(t) {
    // card-win-N unlocks the one-use card prize; founding-waitlist unlocks the founding 10% for life.
    if (cards) return ['card-game', `card-win-${cfg.tiers[t]}`].join(',');
    const tags = ['founding-waitlist', 'founding-member'];
    ['q1', 'q2', 'q3'].forEach((q) => { if (answers[q]) tags.push(`${cfg.tagKeys[q]}-${slug(answers[q].label)}`); });
    return tags.join(',');
  }

  function alertStore(email, t, existing) {
    const alertForm = root.querySelector('[data-fg-alert]');
    if (!alertForm) return;
    const lines = cards
      ? ['Form: Card game', `Email: ${email}`, `Card won: ${cfg.tiers[t]}% off first order (${cfg.codes[t]})`]
      : ['Form: Founding member', `Email: ${email}`, `Founding: 10% for life (${cfg.founding})`];
    // Shopify ignores the sign-up form for people already on the email list, so their tag isn't added.
    // Card codes work for anyone (one use each); FOUNDER10 is locked to the founding-waitlist tag.
    if (existing) lines.push(cards
      ? 'Already on the email list (Shopify did not add tags). Their card code still works: one use per customer.'
      : 'ALREADY SUBSCRIBED: Shopify did not add their tag. Add the tag "founding-waitlist" to this customer in Shopify admin so FOUNDER10 works for them.');
    ['q1', 'q2', 'q3'].forEach((q, i) => { if (answers[q]) lines.push(`Answer ${i + 1}: ${answers[q].label}`); });
    alertForm.querySelector('[data-fg-alert-email]').value = email;
    alertForm.querySelector('[data-fg-alert-body]').value = lines.join('\n');
    fetch(alertForm.action, { method: 'POST', body: new FormData(alertForm), credentials: 'same-origin' }).catch(() => {});
  }

  // Cards go on to the deck; the founding flow ends on "you're in".
  const after = cards ? 'game' : 'joined';
  const remember = () => write(localStorage, PLAYED, JSON.stringify(cards ? { tier: cfg.tiers[tier], code: cfg.codes[tier], at: Date.now() } : { joined: true, at: Date.now() }));

  async function send(e) {
    e.preventDefault();
    if (form.classList.contains('is-sending')) return;
    // Only a genuinely malformed email is ever called wrong; nothing Shopify sends back is.
    const email = cleanEmail(emailField.value);
    emailField.value = email;
    if (!looksLikeEmail(email)) {
      note.textContent = cfg.errorEmail;
      note.classList.add('is-shown');
      emailField.focus();
      return;
    }
    note.classList.remove('is-shown');
    // The draw happens here, once, and never before the email is sent.
    if (tier === null && cards) tier = drawTier(cfg.odds);
    form.querySelector('[data-fg-tags]').value = tagsFor(tier);
    form.classList.add('is-sending');
    if (preview) { form.classList.remove('is-sending'); go(after); return; }
    // Kept until the sign-up is confirmed, so if Shopify has to take over (its human check reloads
    // the page) they come straight back to the same place and, for cards, the same prize.
    write(localStorage, PENDING, JSON.stringify({ tier, email, at: Date.now() }));
    // A normal form post, sent in the background. Nothing Shopify sends back is ever treated as a bad email.
    // - redirected to ?customer_posted=true: signed up.
    // - any other OK page: Shopify received it but the email is already on the list (Shopify ignores
    //   the form for existing subscribers), so they carry on too and the store alert flags it.
    // - redirected to /challenge or a 403: Shopify's human check, handed over below.
    // - anything else (busy, rate limit, offline): "try again", same prize kept.
    let saved = false;
    let existing = false;
    let check = false;
    try {
      const res = await fetch(form.action, { method: 'POST', body: new URLSearchParams(new FormData(form)), credentials: 'same-origin' });
      check = res.status === 403 || /\/challenge/.test(res.url);
      saved = !check && res.ok;
      existing = saved && !/customer_posted=true/.test(res.url);
      console.info('[UPLIFT sign-up] Shopify replied', res.status, res.url.replace(location.origin, ''), check ? '(human check)' : existing ? '(already subscribed)' : '');
    } catch (err) {
      console.info('[UPLIFT sign-up] background post failed', err && err.message);
    }
    if (check) {
      // Shopify wants its human check: its standard post takes over, saves the sign-up after the
      // check and sends them back here, where the pending sign-up resumes on the cards.
      HTMLFormElement.prototype.submit.call(form);
      return;
    }
    if (!saved) {
      // Busy or offline: say so (never "bad email") and keep the same prize for the retry.
      try { localStorage.removeItem(PENDING); } catch (e) {}
      form.classList.remove('is-sending');
      note.textContent = cfg.errorSend;
      note.classList.add('is-shown');
      return;
    }
    try { localStorage.removeItem(PENDING); } catch (e) {}
    form.classList.remove('is-sending');
    alertStore(email, tier, existing);
    remember();
    go(after);
  }
  // The form never fires a "submit" event: Shopify's storefront script re-posts any customer form
  // that does (straight away, ignoring preventDefault), which reloaded the page mid-sign-up. The
  // button and the Enter / Go key call send() instead, and send() does the posting.
  const submit = form.querySelector('[type=submit]');
  submit.type = 'button';
  submit.addEventListener('click', send);
  emailField.addEventListener('keydown', (e) => { if (e.key === 'Enter') send(e); });
  form.addEventListener('submit', (e) => e.preventDefault());

  // The deck: the picked card flips to the drawn prize; the others stay face down and step back.
  const picks = [...root.querySelectorAll('[data-fg-pick]')];
  // Tilt toward the finger or pointer, with a glare that follows it.
  const tilt = (btn, e) => {
    if (btn.disabled || reduced) return;
    const r = btn.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
    btn.style.setProperty('--ry', `${(x - 0.5) * 24}deg`);
    btn.style.setProperty('--rx', `${(0.5 - y) * 24}deg`);
    btn.style.setProperty('--gx', `${x * 100}%`);
    btn.style.setProperty('--gy', `${y * 100}%`);
    btn.classList.add('is-tilting');
  };
  const untilt = (btn) => {
    btn.classList.remove('is-tilting');
    ['--rx', '--ry', '--gx', '--gy'].forEach((k) => btn.style.removeProperty(k));
  };
  picks.forEach((btn) => {
    btn.addEventListener('pointermove', (e) => { if (e.pointerType === 'mouse' || e.buttons) tilt(btn, e); });
    btn.addEventListener('pointerdown', (e) => tilt(btn, e));
    ['pointerleave', 'pointercancel'].forEach((t) => btn.addEventListener(t, () => untilt(btn)));
    btn.addEventListener('click', () => {
      if (tier === null || picks.some((p) => p.classList.contains('is-flipped'))) return;
      picks.forEach((p) => { untilt(p); p.disabled = true; });
      btn.querySelector('[data-fg-pct]').textContent = `${cfg.tiers[tier]}%`;
      btn.classList.add('is-flipped', 'is-won');
      picks.filter((p) => p !== btn).forEach((p) => p.classList.add('is-other'));
      if (navigator.vibrate) navigator.vibrate(18);
      setTimeout(showWin, reduced ? 300 : 2600);
    });
  });

  function showWin() {
    const pct = cfg.tiers[tier];
    root.querySelector('[data-fg-win-pct]').textContent = `${pct}%`;
    root.querySelector('[data-fg-code]').textContent = cfg.codes[tier];
    if (price) {
      const now = Math.round(price * (100 - pct) / 100);
      root.querySelector('[data-fg-price]').innerHTML = `Your first tub: <s>${money(price)}</s> <b>${money(now)}</b>`;
    }
    go('win');
  }

  root.querySelectorAll('[data-fg-copy], [data-fg-copy-code]').forEach((btn) => btn.addEventListener('click', async (e) => {
    const b = e.currentTarget;
    try { await navigator.clipboard.writeText(b.dataset.fgCopyCode || cfg.codes[tier]); } catch (err) {}
    const label = b.textContent;
    b.textContent = b.dataset.done;
    setTimeout(() => { b.textContent = label; }, 1800);
  }));

  // Overlay open and close.
  function open() {
    if (!overlay) return;
    lastFocus = document.activeElement;
    root.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    if (prompt) prompt.hidden = true;
    if (!preview) write(localStorage, SEEN, '1');
    go(current);
  }
  function close() {
    if (!overlay) return;
    root.hidden = true;
    document.documentElement.style.overflow = '';
    if (prompt && current !== 'win') prompt.hidden = !!playedState();
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    if (!cards) document.dispatchEvent(new CustomEvent('up:founding-closed', { detail: { joined: !!playedState() } }));
  }
  root.querySelectorAll('[data-fg-close]').forEach((el) => el.addEventListener('click', close));
  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay) close();
    if (e.key !== 'Tab' || !overlay) return;
    const f = [...card.querySelectorAll('button:not([disabled]), a[href], input:not([type=hidden]), [tabindex="0"]')].filter((el) => el.offsetParent);
    if (!f.length) return;
    if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
    else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
  });
  if (prompt) prompt.addEventListener('click', open);
  document.addEventListener('click', (e) => {
    const a = e.target.closest(cards ? 'a[href$="#pick-a-card"]' : 'a[href$="#founding-sign-up"]');
    if (a && overlay && !playedState()) { e.preventDefault(); open(); }
  });

  // Back from Shopify's standard post (and its human check, if it asked): pick up where they left off.
  const pending = (() => { try { return JSON.parse(read(localStorage, PENDING) || 'null'); } catch (e) { return null; } })();
  // Shopify marks a saved sign-up with ?customer_posted=true; after its human check it returns to
  // the same page (#contact_form, coming from /challenge).
  const backFromShopify = /customer_posted=true/.test(location.search) || (location.hash === '#contact_form' && /\/challenge/.test(document.referrer));
  if (pending && backFromShopify && Date.now() - (pending.at || 0) < 30 * 60 * 1000) {
    try { localStorage.removeItem(PENDING); } catch (e) {}
    tier = pending.tier;
    if (pending.email) alertStore(pending.email, tier);
    remember();
    if (overlay) { root.hidden = false; document.documentElement.style.overflow = 'hidden'; }
    go(after);
    return;
  }

  // Card pop-up set to follow the founding pop-up: founding members never get it; everyone else
  // gets it right after closing the founding pop-up (or on load, if they closed that one before).
  const founding = cards && overlay && root.dataset.after === 'founding'
    ? document.querySelector('[data-up-fg][data-flow="founding"][data-mode="overlay"]')
    : null;
  if (founding) {
    if (read(localStorage, 'up_fg_played') && !preview) { if (prompt) prompt.hidden = true; return; }
    if (preview || !read(localStorage, 'up_fg_seen')) {
      document.addEventListener('up:founding-closed', (e) => {
        if (e.detail.joined) { if (prompt) prompt.hidden = true; return; }
        if (preview || (!playedState() && !read(localStorage, SEEN))) whenConsentClear(() => setTimeout(open, reduced ? 0 : 450));
        else if (prompt && !playedState()) prompt.hidden = false;
      });
      return;
    }
  }

  if (preview) {
    if (overlay) open();
    return;
  }

  const played = playedState();
  if (played) {
    // Already played in this browser: no overlay, no prompt. Inline shows their result again.
    if (!overlay) {
      if (!cards) { go('joined'); return; }
      const t = cfg.tiers.indexOf(played.tier);
      if (t >= 0) { tier = t; showWin(); }
    }
    return;
  }
  if (!overlay) return;
  if (!read(localStorage, SEEN) && !new URLSearchParams(location.search).has('nogate')) {
    // Wait for the page to finish loading and the browser to be idle, so the overlay never competes with the first paint.
    const later = () => whenConsentClear(() => (window.requestIdleCallback ? requestIdleCallback(() => setTimeout(open, 600), { timeout: 2500 }) : setTimeout(open, 1200)));
    if (document.readyState === 'complete') later(); else addEventListener('load', later, { once: true });
  } else if (prompt) {
    // Not pushy: the prompt waits until they've scrolled a little.
    const reveal = () => { if (window.scrollY > innerHeight * 0.6 || (document.querySelector('.page-wrapper')?.scrollTop || 0) > innerHeight * 0.6) { prompt.hidden = false; cleanup(); } };
    const cleanup = () => { window.removeEventListener('scroll', reveal); document.querySelector('.page-wrapper')?.removeEventListener('scroll', reveal); };
    window.addEventListener('scroll', reveal, { passive: true });
    document.querySelector('.page-wrapper')?.addEventListener('scroll', reveal, { passive: true });
  }
}

document.querySelectorAll('[data-up-fg]').forEach(init);
